'use client'

import { BuilderBlocks } from '@builder.io/react'
import { useMemo, useState } from 'react'

import { Text } from '@src/components/Builder/Text/Text'
import { Button } from '@src/components/Button/Button'
import { Container } from '@src/components/Container/Container'
import { CustomerReviewCard } from '@src/components/CustomerReviews/CustomerReviewCard'
import { CustomerReviewsDefaults } from '@src/components/CustomerReviews/CustomerReviews.defaults'
import {
  type CustomerReviewItem,
  type CustomerReviewsProps,
} from '@src/components/CustomerReviews/CustomerReviews.props'
import { StarRating } from '@src/components/CustomerReviews/StarRating'
import { useBreakpoints } from '@src/utils/breakpoints.util'

export const CustomerReviews = (props: CustomerReviewsProps = CustomerReviewsDefaults) => {
  const {
    accentColor,
    blocksBottom,
    builderBlock,
    gradientColor,
    heading,
    items = [],
    loadMoreText,
    subheading,
    subHeadingRatingOutOfFive,
  } = props
  const [sliceIncrement, setSliceIncrement] = useState(0)
  const breakpoints = useBreakpoints()
  const isMobile = breakpoints.isMobile || breakpoints.isTablet

  const columns = useMemo(() => {
    if (!isMobile) {
      const first = items.filter((_, i) => i % 3 === 0).slice(0, 2 + sliceIncrement)
      const second = items.filter((_, i) => i % 3 === 1).slice(0, 2 + sliceIncrement)
      const third = items.filter((_, i) => i % 3 === 2).slice(0, 2 + sliceIncrement)

      return {
        first,
        second,
        third,
      }
    }
    const first = [] as CustomerReviewItem[]
    const second = [] as CustomerReviewItem[]
    const third = items.slice(0, 3 + sliceIncrement * 3)

    return {
      first,
      second,
      third,
    }
  }, [isMobile, items, sliceIncrement])

  const hasMore =
    items.length > columns?.first?.length + columns?.second?.length + columns?.third?.length

  const onLoadMoreClick = () => {
    setSliceIncrement((current) => current + 1)
  }

  const renderColumns = () => {
    const wrapperClasses = 'grid gap-4 lg:gap-10'

    const first = columns?.first
    const second = columns?.second
    const third = columns?.third

    return (
      <>
        {first?.length > 0 && <div className={wrapperClasses}>{first.map(renderCard)}</div>}
        {second?.length > 0 && <div className={wrapperClasses}>{second.map(renderCard)}</div>}
        {third?.length > 0 && <div className={wrapperClasses}>{third.map(renderCard)}</div>}
      </>
    )
  }

  const renderCard = (item: CustomerReviewItem, i: number) => {
    return (
      <CustomerReviewCard
        color={accentColor}
        content={item.content}
        key={i}
        name={item.name}
        product={item.product}
        ratingOutOfFive={item.ratingOutOfFive}
        shouldAnimate={sliceIncrement > 0}
      />
    )
  }

  return (
    <div
      className="relative bg-white py-28"
      style={{
        backgroundImage: `radial-gradient(at center, ${gradientColor} 0%, transparent 80%)`,
      }}
    >
      <Container className="bg-transparent">
        <h2 className="text-heading-display-2 relative mb-1 text-center text-bodi-black-text">
          {heading}
        </h2>
        <div className="relative mb-6 flex items-center justify-center gap-2">
          <StarRating color={accentColor} ratingOutOfFive={subHeadingRatingOutOfFive ?? 0} />
          <div
            dangerouslySetInnerHTML={{ __html: subheading ?? '' }}
            style={{ color: accentColor }}
          ></div>
        </div>
        <div
          className="
          grid items-start lg:grid-cols-3 lg:gap-10"
        >
          {renderColumns()}
        </div>
        <div className="relative mt-8 mb-6 lg:mt-16">
          <BuilderBlocks
            parentElementId={builderBlock?.id}
            dataPath="component.options.contentBottom"
            blocks={blocksBottom}
          />
        </div>
        {hasMore && (
          <Button
            className="mx-auto block"
            size="medium"
            variant="secondary"
            onClick={onLoadMoreClick}
          >
            <Text style="Body Base" text={loadMoreText ?? ''} />
          </Button>
        )}
      </Container>
    </div>
  )
}
